/**
 * Batch 18 GRF-53 (ruling R7 A, ruling 2 of 11 Oct 2026; design §6), behind `Features__SummaryValidation`: a summary and a
 * description must read as words. The server is the authority (`WorkItemTextRules`); this mirror gives the same answer at once,
 * in the same words. Normative spec and vectors: shared/text-quality/ (textQuality.test.ts runs every vector).
 */

export type TextQualityCode = "repeated_character" | "too_short" | "no_letter";

export const MIN_CODE_POINTS = 5;

export const TEXT_QUALITY_MESSAGES: Record<TextQualityCode, string> = {
  repeated_character: "This looks like one character repeated. Describe the work in words.",
  too_short: "Write at least 5 characters, for example what is wrong and where.",
  no_letter: "Add words, not only numbers or symbols.",
};

const HTML_COMMENT = /<!--[\s\S]*?-->/g;
const MARKDOWN_IMAGE = /!\[[^\]]*\]\([^)]*\)/g;
const HTML_IMAGE = /<img\b[^>]*>/gi;
const MARKDOWN_LINK = /\[([^\]]*)\]\([^)]*\)/g;
const ADDRESS = /<https?:\/\/[^>\s]*>|https?:\/\/\S+/gi;
const WHITE_SPACE = /\s/u;
const LETTER = /\p{L}/u;

function check(text: string): TextQualityCode | null {
  // Code points, not UTF-16 units: Thai marks count ("แก้ไข" is 5) and an astral emoji is one.
  const codePoints = [...text.trim()];
  const visible = codePoints.filter((c) => !WHITE_SPACE.test(c));
  if (visible.length >= 2 && visible.every((c) => c === visible[0])) return "repeated_character";
  if (codePoints.length < MIN_CODE_POINTS) return "too_short";
  return codePoints.some((c) => LETTER.test(c)) ? null : "no_letter";
}

/** The summary's problem, or null. A blank summary is the form's own "required" check. */
export function summaryProblem(summary: string): TextQualityCode | null {
  return summary.trim() === "" ? null : check(summary);
}

/** The description's problem, or null. Empty, or only pictures and links, is no description and passes (ruling 2 (a)). */
export function descriptionProblem(description: string): TextQualityCode | null {
  if (description.trim() === "") return null;
  const withoutPictures = description.replace(HTML_COMMENT, " ").replace(MARKDOWN_IMAGE, " ").replace(HTML_IMAGE, " ");
  if (withoutPictures.replace(MARKDOWN_LINK, " ").replace(ADDRESS, " ").trim() === "") return null;
  return check(withoutPictures.replace(MARKDOWN_LINK, "$1").replace(ADDRESS, " "));
}

export interface TextQualityBaseline {
  summary: string;
  description: string;
}

/**
 * The words for each refused field, keyed "summary" / "description". `baseline` (edit): a field still holding the value the item
 * already has is not checked, as the server never checks a field the request does not change — an old "aaa" never blocks an edit.
 */
export function textQualityErrors(values: TextQualityBaseline, baseline?: TextQualityBaseline): Partial<Record<keyof TextQualityBaseline, string>> {
  const errors: Partial<Record<keyof TextQualityBaseline, string>> = {};
  const summary = baseline && values.summary.trim() === baseline.summary.trim() ? null : summaryProblem(values.summary);
  if (summary) errors.summary = TEXT_QUALITY_MESSAGES[summary];
  const description = baseline && values.description === baseline.description ? null : descriptionProblem(values.description);
  if (description) errors.description = TEXT_QUALITY_MESSAGES[description];
  return errors;
}
